import { useCallback } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
    BULK_CLEAR_NOTIFICATIONS,
    UPDATE_SINGLE_NOTIFICATION_READ_STATUS,
    DELETE_SINGLE_NOTIFICATION,
} from "adapters/notification.service";
import { GENERATE_REGENRATE_TEMPLATE } from "adapters/recognizer.service";
import { clear_notification, delete_notification } from "redux/actions/AppActions";
import { set_show_popup } from "redux/actions/ScribeAcitons";
import { encounterObject } from "redux/actions/EncounterActions";
import { extractPatientAndEncounterIdFromComposition } from "config/helpers/utils";
import { getCurrentRecordingState } from "config/helpers/recordingStateSelector";
import { getCurrentProgressState } from "config/helpers/progressStateSelector";
import { TaskProcessorRegistry } from "config/helpers/backgroundTaskUtils/backgroundTaskUtils";
import { startWatchingTask } from "redux/redux-thunk/BackgroundtaskThunks";
import { isTaskInProgress } from "./NotificationHelpers";

export const useNotificationActions = ({
    currentPractitionerId,
    allNotifications,
    setIsUpdating,
    loadNotifications,
    onClose,
    navigate,
    dispatch,
    t,
    setServerNotifications, // Add this to enable optimistic updates
}) => {
    const patientObj = useSelector((state) => state?.patientObj);
    const encounterObj = useSelector((state) => state?.encounterObj);

    const { recording: isRecording, paused: isPaused } = useSelector((state) =>
        getCurrentRecordingState(
            state,
            patientObj?.patientObj?.id,
            encounterObj?.encounter?.id
        )
    );

    const progressState = useSelector((state) =>
        getCurrentProgressState(
            state,
            patientObj?.patientObj?.id,
            encounterObj?.encounter?.id
        )
    );
    const { isProgressReady } = progressState || {};

    // Clear all notifications
    const clearAll = useCallback(async () => {
        try {
            setIsUpdating(true);

            // Get ALL background notifications (both read and unread) - excluding in-progress
            const allBackgroundNotifications = allNotifications.filter(
                n => n.type === "background_task" &&
                    n.basicResourceId &&
                    n.taskId &&
                    ["COMPLETED", "FAILED"].includes(n.status)
            );

            // OPTIMISTIC UPDATE: Immediately remove from UI
            if (setServerNotifications) {
                setServerNotifications(prev =>
                    prev.filter(n =>
                        !(n.type === "background_task" &&
                            n.basicResourceId &&
                            n.taskId &&
                            ["COMPLETED", "FAILED"].includes(n.status))
                    )
                );
            }

            // Clear all local toast notifications immediately
            dispatch(clear_notification());

            // Show success message
            toast.success(t("notifications.ALL_CLEARED"), {
                data: { ignoreNotification: true },
            });

            // Now do server update in background
            if (allBackgroundNotifications.length > 0) {
                await BULK_CLEAR_NOTIFICATIONS(allBackgroundNotifications, currentPractitionerId);
            }

            // Final refresh to sync with server
            await loadNotifications();
        } catch (error) {
            console.error("Failed to clear notifications:", error);
            toast.error(t("common.ERROR"))
            await loadNotifications();
        } finally {
            setIsUpdating(false);
        }
    }, [allNotifications, currentPractitionerId, dispatch, t, setIsUpdating, loadNotifications, setServerNotifications]);

    // Delete single notification
    const handleDelete = useCallback(async (notification) => {
        try {
            setIsUpdating(true);

            if (notification.type === "background_task" && setServerNotifications) {
                setServerNotifications(prev =>
                    prev.filter(n =>
                        !(n.basicResourceId === notification.basicResourceId &&
                            n.taskId === notification.taskId)
                    )
                );
            } else {
                dispatch(delete_notification(notification.addedOn));
            }

            if (notification.type === "background_task" && notification.basicResourceId && notification.taskId) {
                await DELETE_SINGLE_NOTIFICATION(notification, currentPractitionerId);
            }

            await loadNotifications();
        } catch (error) {
            console.error("Failed to delete notification:", error);
            toast.error(t("common.ERROR"));
            await loadNotifications();
        } finally {
            setIsUpdating(false);
        }
    }, [currentPractitionerId, dispatch, t, setIsUpdating, loadNotifications, setServerNotifications]);

    // Mark as unread (user-triggered, shows a success toast)
    const handleMarkAsUnread = useCallback(async (notification) => {
        try {
            setIsUpdating(true);

            if (setServerNotifications) {
                setServerNotifications(prev =>
                    prev.map(n =>
                        (n.basicResourceId === notification.basicResourceId &&
                            n.taskId === notification.taskId)
                            ? { ...n, isRead: false }
                            : n
                    )
                );
            }

            toast.success(t("notifications.MARKED_AS_UNREAD_SUCCESS"), {
                data: { ignoreNotification: true },
            });

            await UPDATE_SINGLE_NOTIFICATION_READ_STATUS(notification, false, currentPractitionerId);
            await loadNotifications();
        } catch (error) {
            console.error("Failed to mark as unread:", error);
            toast.error(t("common.ERROR"));
            await loadNotifications();
        } finally {
            setIsUpdating(false);
        }
    }, [currentPractitionerId, t, setIsUpdating, loadNotifications, setServerNotifications]);


    const handleMarkAsRead = useCallback(async (notification) => {
        if (
            notification.type !== "background_task" ||
            notification.isRead ||
            !notification.basicResourceId ||
            !notification.taskId
        ) {
            return;
        }

        try {
            setIsUpdating(true);

            if (setServerNotifications) {
                setServerNotifications(prev =>
                    prev.map(n =>
                        (n.basicResourceId === notification.basicResourceId &&
                            n.taskId === notification.taskId)
                            ? { ...n, isRead: true }
                            : n
                    )
                );
            }

            await UPDATE_SINGLE_NOTIFICATION_READ_STATUS(notification, true, currentPractitionerId);
            await loadNotifications();
        } catch (error) {
            console.error("Failed to auto-mark notification as read:", error);
            // Silently restore — no error toast since this is an invisible auto-action
            await loadNotifications();
        } finally {
            setIsUpdating(false);
        }
    }, [currentPractitionerId, setIsUpdating, loadNotifications, setServerNotifications]);
    // ────────────────────────────────────────────────────────────────────────────

    const handleClick = useCallback(async (task) => {
        onClose();
        if (isTaskInProgress(task.status)) {
            return;
        }

        const isEncounterArchived =
            task.task_category === "soap" &&
            task?.encounter?.status === "entered-in-error";

        if (isEncounterArchived) {
            return;
        }

        let navigateTo = null;
        let navigateState = {};

        if (
            task.type === "background_task" &&
            !task.isRead &&
            task.basicResourceId &&
            ["COMPLETED", "FAILED"].includes(task.status)
        ) {
            try {
                setIsUpdating(true);

                if (setServerNotifications) {
                    setServerNotifications(prev =>
                        prev.map(n =>
                            (n.basicResourceId === task.basicResourceId &&
                                n.taskId === task.taskId)
                                ? { ...n, isRead: true }
                                : n
                        )
                    );
                }

                await UPDATE_SINGLE_NOTIFICATION_READ_STATUS(task, true, currentPractitionerId);
                await loadNotifications();
            } catch (error) {
                console.error("Failed to mark as read:", error);
                await loadNotifications();
            } finally {
                setIsUpdating(false);
            }
        }

        if (task.type === "background_task" && task.task_category === "template") {
            const template = TaskProcessorRegistry[task.task_category]?.extractFinalPayload(task?.taskResource);

            if (template) {
                navigateTo = `/customForms/templates/${template?.templateCode}`;
                navigateState = { state: { data: template, isGenerated: true } };

                if (isRecording || isPaused) {
                    dispatch(set_show_popup(true, navigateTo, navigateState, task?.id));
                    return;
                }
            }
        } else if (task.type === "background_task" && task.task_category === "soap") {
            const soapData = TaskProcessorRegistry[task.task_category]?.extractFinalPayload(task?.taskResource);

            if (soapData) {
                let patientId = soapData?.patientId;
                let encounterId = soapData?.encounterId;

                if (!patientId || !encounterId) {
                    const extracted = extractPatientAndEncounterIdFromComposition(soapData || {});
                    patientId = extracted.soapPatientId;
                    encounterId = extracted.soapEncounterId;
                }

                const tabId = soapData?.status === "FAILED" ? 1 : 2;
                const visitId = encounterId || soapData?.encounterId;

                navigateTo = `/patients/visits/${patientId || soapData?.patientId}?tabId=${tabId}&visitId=${visitId}`;

                if ((isRecording || isPaused) && !isProgressReady) {
                    dispatch(set_show_popup(true, { visitId, taskId: soapData?.id }));
                    return;
                }

                dispatch(encounterObject({ id: visitId, composition: { ...soapData } }));
            }
        }

        if (navigateTo) {
            navigate(navigateTo, navigateState);
            onClose();
        }
    }, [
        currentPractitionerId,
        isRecording,
        isPaused,
        isProgressReady,
        setIsUpdating,
        loadNotifications,
        dispatch,
        navigate,
        onClose,
        setServerNotifications,
    ]);

    // Retry failed task
    const handleRetry = useCallback(async (notification) => {
        const { taskId, id, task_category, encounterId, payload } = notification;
        const actualTaskId = taskId || id;

        try {
            if (task_category === "soap" && encounterId) {
                const retryTaskId = `${actualTaskId}_retry_${Date.now()}`;
                setIsUpdating(true);

                if (notification.basicResourceId) {
                    await DELETE_SINGLE_NOTIFICATION(notification, currentPractitionerId);
                }

                dispatch(startWatchingTask(retryTaskId, task_category, () => { }));

                await GENERATE_REGENRATE_TEMPLATE(
                    "generate-encounter-soap-note",
                    encounterId,
                    payload || {}
                );

                toast.success(t("notifications.RETRY_INITIATED"), {
                    data: { ignoreNotification: true },
                });

                await loadNotifications();
            }
        } catch (error) {
            console.error("Failed to retry task:", error);
            toast.error(t("common.ERROR"));
        } finally {
            setIsUpdating(false);
        }
    }, [currentPractitionerId, dispatch, t, setIsUpdating, loadNotifications]);

    return {
        clearAll,
        handleDelete,
        handleMarkAsUnread,
        handleMarkAsRead,   // ← new
        handleClick,
        handleRetry,
    };
};